import { LinkButtonWithIcon } from "@/components/Buttons/LinkButtonWithIcon"
import FormattedMinMaxUnit from "@/components/FormattedMinMaxUnit/FormattedMinMaxUnit"
import { FormattedPriceRange } from "@/components/FormattedPrice"
import { TagIcon } from "@/components/Icons"
import { EyeIcon } from "@/components/Icons"
import { RefLabel } from "@/components/RefLabel"
import { SheetField, SheetFieldGrid, SheetSection } from "@/components/Sections/SheetSection"
import { P3 } from "@/components/Typographies"
import { getDisplayedName } from "@/domain/people/getDisplayedName"
import { getPeopleLinkSheet } from "@/domain/people/getPeopleLinkSheet"
import type { SharedDataJsonldReadCollectionSharedDataInterBu } from "@/networking/api"
import { ADDRESS_FORMAT, formatAddress } from "@/utils/address/formatAddress"
import { isNotBlank, isNotNullNorUndefined } from "@/utils/ts-utils"
import { useIntl } from "react-intl"
import { useNavigate } from "react-router-dom"

type Props = {
  interBu: SharedDataJsonldReadCollectionSharedDataInterBu
}

export const InterBUSection = ({ interBu }: Props) => {
  const intl = useIntl()
  const navigateTo = useNavigate()

  return (
    <SheetSection
      title={intl.formatMessage({ id: "userSharedData.interbu.details" })}
      icon={TagIcon}
      isFirst
    >
      <SheetFieldGrid>
        {isNotNullNorUndefined(interBu?.people) && (
          <SheetField label={intl.formatMessage({ id: "userSharedData.interbu.people.name" })}>
            {getDisplayedName(interBu?.people)}
            <LinkButtonWithIcon
              display="inline-block"
              ml="2"
              icon={EyeIcon}
              onClick={() => navigateTo(getPeopleLinkSheet(interBu?.people))}
            />
          </SheetField>
        )}
        {isNotNullNorUndefined(interBu?.projectMaturity) && (
          <SheetField label={intl.formatMessage({ id: "userSharedData.interbu.project_maturity" })}>
            <RefLabel label={interBu?.projectMaturity} />
          </SheetField>
        )}
        {(isNotNullNorUndefined(interBu?.type) || isNotNullNorUndefined(interBu?.subtype)) && (
          <SheetField
            label={intl.formatMessage({ id: "clientList.advancedFilters.projects.projectType" })}
          >
            <RefLabel label={interBu?.type} />
            {isNotNullNorUndefined(interBu?.subtype) && (
              <>
                / <RefLabel label={interBu?.subtype} />
              </>
            )}
          </SheetField>
        )}
        {isNotNullNorUndefined(interBu?.propertyType) && (
          <SheetField label={intl.formatMessage({ id: "userSharedData.interbu.property.type" })}>
            <RefLabel label={interBu?.propertyType} />
          </SheetField>
        )}
        {((interBu?.priceMin ?? 0) > 0 || (interBu?.priceMax ?? 0) > 0) && (
          <SheetField label={intl.formatMessage({ id: "userSharedData.interbu.price" })}>
            <FormattedPriceRange
              min={interBu?.priceMin}
              max={interBu?.priceMax}
              currency={interBu?.priceUnit}
            />
          </SheetField>
        )}
        {((interBu?.surfaceMin ?? 0) > 0 || (interBu?.surfaceMax ?? 0) > 0) && (
          <SheetField label={intl.formatMessage({ id: "userSharedData.interbu.property.surface" })}>
            <FormattedMinMaxUnit
              min={interBu?.surfaceMin}
              max={interBu?.surfaceMax}
              unit={interBu?.surfaceUnit}
            />
          </SheetField>
        )}
        {(isNotBlank(interBu?.zipcode ?? "") || isNotBlank(interBu?.city ?? "")) && (
          <SheetField label={intl.formatMessage({ id: "userSharedData.interbu.geoloc" })}>
            {/* Street on the first line, "zipcode city (country)" on the second. */}
            {[ADDRESS_FORMAT.ADD_123, ADDRESS_FORMAT.CP_CITY_COUNTRY].map(format => {
              const line = formatAddress(
                {
                  id: "",
                  address1: interBu?.address1,
                  address2: interBu?.address2,
                  address3: interBu?.address3,
                  zipcode: interBu?.zipcode,
                  city: interBu?.city,
                  country: interBu?.country,
                },
                format,
                ""
              )
              return isNotBlank(line) ? (
                <P3 key={format} display="block">
                  {line}
                </P3>
              ) : null
            })}
          </SheetField>
        )}
      </SheetFieldGrid>
    </SheetSection>
  )
}
